
import {
  IndexTable,
  LegacyCard,
  IndexFilters,
  useSetIndexFiltersMode,
  useIndexResourceState,
  Text,
  Link,
  Page,
  Avatar,
  HorizontalStack,
  EmptyState,
  Spinner
} from '@shopify/polaris';
import { useState, useCallback, useEffect } from 'react';
import { markBulkAsRead } from '../../firebase';
import { usePartnerUp } from '../../providers/PartnerUpProvider';

export default function DealsTable({ loading }) {

  let { deals, setDeals } = usePartnerUp();

  const sleep = (ms) =>
    new Promise((resolve) => setTimeout(resolve, ms));
  const [itemStrings, setItemStrings] = useState([
    'Inbox',
    'Sent',
    'Published',
    'Archive'
  ]);

  const [selected, setSelected] = useState(0);
  const onCreateNewView = async (value) => {
    await sleep(500);
    setItemStrings([...itemStrings, value]);
    setSelected(itemStrings.length);
    return true;
  };
  const sortOptions = [
    { label: 'Date', value: 'date asc', directionLabel: 'Newest' },
    { label: 'Date', value: 'date desc', directionLabel: 'Oldest' },
    { label: 'Brand', value: 'brand asc', directionLabel: 'A-Z' },
    { label: 'Brand', value: 'brand desc', directionLabel: 'Z-A' },
    { label: 'Value', value: 'value asc', directionLabel: 'Highest' },
    { label: 'Value', value: 'value desc', directionLabel: 'Lowest' },
    { label: 'Status', value: 'status asc', directionLabel: 'A-Z' },
    { label: 'Status', value: 'status desc', directionLabel: 'Z-A' },
  ];
  const [sortSelected, setSortSelected] = useState(['date asc']);
  const { mode, setMode } = useSetIndexFiltersMode();
  const onHandleCancel = () => {
    setMode('view');
  };

  const [accountStatus, setAccountStatus] = useState(
    undefined,
  );
  const [moneySpent, setMoneySpent] = useState(
    undefined,
  );
  const [taggedWith, setTaggedWith] = useState('');
  const [queryValue, setQueryValue] = useState('');

  const handleFiltersQueryChange = useCallback(
    (value) => setQueryValue(value),
    [],
  );
  const handleAccountStatusRemove = useCallback(
    () => setAccountStatus(undefined),
    [],
  );
  const handleMoneySpentRemove = useCallback(
    () => setMoneySpent(undefined),
    [],
  );
  const handleTaggedWithRemove = useCallback(() => setTaggedWith(''), []);
  const handleQueryValueRemove = useCallback(() => setQueryValue(''), []);
  const handleFiltersClearAll = useCallback(() => {
    handleAccountStatusRemove();
    handleMoneySpentRemove();
    handleTaggedWithRemove();
    handleQueryValueRemove();
  }, [
    handleAccountStatusRemove,
    handleMoneySpentRemove,
    handleQueryValueRemove,
    handleTaggedWithRemove,
  ]);

  const appliedFilters = [];
  if (accountStatus && !isEmpty(accountStatus)) {
    const key = 'accountStatus';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, accountStatus),
      onRemove: handleAccountStatusRemove,
    });
  }
  if (moneySpent) {
    const key = 'moneySpent';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, moneySpent),
      onRemove: handleMoneySpentRemove,
    });
  }
  if (!isEmpty(taggedWith)) {
    const key = 'taggedWith';
    appliedFilters.push({
      key,
      label: disambiguateLabel(key, taggedWith),
      onRemove: handleTaggedWithRemove,
    });
  }

  const resourceName = {
    singular: 'deal',
    plural: 'deals',
  };


  // search
  if (queryValue) {
    deals = deals.filter((deal) => {
      return deal.customer.toLowerCase().includes(queryValue.toLowerCase()) || deal.message.toLowerCase().includes(queryValue.toLowerCase());
    })
  }


  // tab
  const folderCounts = { "Inbox": 0, Sent: 0, Published: 0, Archived: 0 };
  deals = deals.filter((deal) => {

    let folder;
    if (deal.status === 'withdrawn' || deal.status === 'rejected') {
      folder = 'Archived';
      if (deal.isUnread && deal.actionRequiredBy !== 'shop') {
        folderCounts.Archived++;
      }
    } else if (deal.status === 'published') {
      folder = 'Published';
      folderCounts.Published++;
    } else if (deal.actionRequiredBy === 'shop') {
      folder = 'Sent';
      folderCounts.Sent++;
    } else {
      folder = 'Inbox';
      if (deal.isUnread) {
        folderCounts["Inbox"]++;
      }
    }

    if (itemStrings[selected] === 'Active') {
      return folder !== 'Archived';
    }

    return folder.toLowerCase().includes(itemStrings[selected].toLowerCase());
  });

  const tabs = itemStrings.map((item, index) => ({
    content: folderCounts[item] ? `${item} (${folderCounts[item]})` : item,
    index,
    onAction: () => { },
    id: `${item}-${index}`,
    isLocked: index === 0,
    actions: []
  }));

  // sort
  deals = deals.sort((a, b) => {
    const [key, direction] = sortSelected[0].split(' ');
    if (key === 'date') {
      return direction === 'desc' ? new Date(a.lastUpdated) - new Date(b.lastUpdated) : new Date(b.lastUpdated) - new Date(a.lastUpdated);
    } else if (key === 'brand') {
      return direction === 'asc' ? a.shopName?.localeCompare(b.shopName) : b.shopName?.localeCompare(a.shopName);
    } else if (key === 'value') {
      return direction === 'desc' ? a.currentValue - b.currentValue : b.currentValue - a.currentValue;
    } else if (key === 'status') {
      return direction === 'asc' ? a.status?.localeCompare(b.status) : b.status?.localeCompare(a.status);
    }
  })

  const { selectedResources, allResourcesSelected, handleSelectionChange } = useIndexResourceState(deals);

  function getStatus(statusString) {
    if (statusString === 'offerSent') {
      return 'Offer Received'
    }
    if (statusString === 'accepted') {
      return 'Awaiting Payment'
    }
    if (statusString === 'rejected') {
      return 'Rejected'
    }
    if (statusString === 'withdrawn') {
      return 'Withdrawn'
    }
    if (statusString === 'paid') {
      return 'Paid'
    }
    if (statusString === 'review') {
      return 'Submitted for Review'
    }
    if (statusString === 'approved') {
      return 'Approved - Deploy Now'
    }
    if (statusString === 'published') {
      return 'Published'
    }
  }

  const rowMarkup = deals.map(
    (
      { id, lastUpdated, lastMessage, shopName, status, currentValue, actionRequiredBy, isUnread },
      index,
    ) => {

      const unread = isUnread && actionRequiredBy === 'influencer';

      return (
        <IndexTable.Row
          id={id}
          key={id}
          selected={selectedResources.includes(id)}
          position={index}
          status={unread ? '' : 'subdued'}
        >
          <IndexTable.Cell>
            <HorizontalStack gap="2" blockAlign="center">
              {/*<Avatar shape="square" customer size="large" name={brandName} />*/}
              <Link
                dataPrimaryLink
                removeUnderline
                url={`/deals/${id}`}
              >
                <Text fontWeight={unread ? "bold" : "regular"} as="span">
                  {shopName}
                </Text>
              </Link>
            </HorizontalStack>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Text fontWeight={unread ? "bold" : "regular"} as="span">
              {getStatus(status)}
            </Text>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Text fontWeight={unread ? "bold" : "regular"} as="span">
              ${currentValue.toLocaleString()}
            </Text>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Text fontWeight={unread ? "bold" : "regular"} as="span">
              {lastMessage}
            </Text>
          </IndexTable.Cell>
          <IndexTable.Cell>
            <Text fontWeight={unread ? "bold" : "regular"} as="span">
              {new Date(lastUpdated).toDateString()}
            </Text>
          </IndexTable.Cell>
        </IndexTable.Row>
      )
    },
  );

  if (loading) {
    return (
      <Page title="Your Deals">
        <LegacyCard>
          <Spinner />
        </LegacyCard>
      </Page>
    )
  }

  return (
    <Page title="Your Deals">
      <LegacyCard>
        <IndexFilters
          sortOptions={sortOptions}
          sortSelected={sortSelected}
          queryValue={queryValue}
          queryPlaceholder={`Searching in ${itemStrings[selected]}...`}
          onQueryChange={handleFiltersQueryChange}
          onQueryClear={() => {
            setQueryValue('');
            handleFiltersQueryChange('');
          }}
          onSort={setSortSelected}
          // primaryAction={primaryAction}
          cancelAction={{
            onAction: onHandleCancel,
            disabled: false,
            loading: false,
          }}
          tabs={tabs}
          selected={selected}
          onSelect={setSelected}
          canCreateNewView={false}
          onCreateNewView={onCreateNewView}
          filters={[]}
          appliedFilters={[]}
          onClearAll={handleFiltersClearAll}
          mode={mode}
          setMode={setMode}
          hideQueryField
          hideFilters
        />
        <IndexTable
          promotedBulkActions={itemStrings[selected] !== 'Archived' && selectedResources.every(dealID => deals.find(d => d.id == dealID)?.status === 'offerSent') ? [
            {
              content: 'Reject Offer(s)',
              onAction: () => {
                // are you sure modal
                // rejectOffers(selectedResources);
              },
            },
          ] : [
            {
              content: 'Mark as Read',
              onAction: async () => {
                try {
                  await markBulkAsRead(selectedResources);
                  setDeals(deals.map(d => {
                    d.isUnread = false;
                    return d;
                  }))
                } catch (e) {
                  console.log(e)
                }
              }
            }
          ]}
          resourceName={resourceName}
          itemCount={deals.length}
          selectedItemsCount={
            allResourcesSelected ? 'All' : selectedResources.length
          }
          onSelectionChange={handleSelectionChange}
          headings={[
            { title: 'Brand' },
            { title: 'Status' },
            { title: 'Value' },
            { title: 'Last Message' },
            { title: 'Updated On' },
          ]}
          emptyState={
            <EmptyState
              heading="Nothing Here"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>There are no deals in this tab. Wait for offers, or check the archived tab!</p>
            </EmptyState>
          }
        >
          {rowMarkup}
        </IndexTable>
      </LegacyCard >
    </Page>
  );

  function disambiguateLabel(key, value) {
    switch (key) {
      case 'moneySpent':
        return `Money spent is between $${value[0]} and $${value[1]}`;
      case 'taggedWith':
        return `Tagged with ${value}`;
      case 'accountStatus':
        return (value).map((val) => `Customer ${val}`).join(', ');
      default:
        return value;
    }
  }

  function isEmpty(value) {
    if (Array.isArray(value)) {
      return value.length === 0;
    } else {
      return value === '' || value == null;
    }
  }
}
